import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    ArrowClockwise,
    BoxArrowUpRight,
    ExclamationTriangleFill,
    FolderFill,
    PencilSquare,
    PlusCircle,
    Trash,
} from 'react-bootstrap-icons';
import Alert from 'react-bootstrap/Alert';
import Modal from 'react-bootstrap/Modal';
import Form from 'react-bootstrap/Form';

import { useProjects } from '../contexts/ProjectContext';
import { useAuth } from '../contexts/AuthContext';
import { ProjectTestCaseCategorySummary, ProjectWithRepository } from '../types/project';
import {
    fetchProjectDeletionImpact,
    fetchProjectsPaged,
    ProjectsFilterParams,
    renameProject,
} from '../api/projects';
import { fetchOrganizations } from '../api/organizations';
import CustomDropdown, { CustomDropdownOption } from '../components/CustomDropdown';
import {
    canAccessAdminArea,
    canCreateProject,
    isActiveOrganizationAdmin,
    isSuperAdminUser,
} from '../utils/roles';
import { ProjectDeletionImpact } from '../types/project';

import './Projects-new.css';

/* ============================================================
   Helpers
   ============================================================ */

/**
 * buildSkeletonCards
 * Returns placeholder cards for loading state.
 */
function buildSkeletonCards(count: number): React.ReactElement[]
{
    const cards: React.ReactElement[] = [];
    for (let i = 0; i < count; i += 1)
    {
        cards.push(
            <div className="projects-new-card projects-new-card-skeleton" key={`skeleton-${i}`}>
                <div className="projects-new-card-header">
                    <div className="projects-new-skeleton-title" />
                    <div className="projects-new-skeleton-pill" />
                </div>
                <div className="projects-new-skeleton-line" />
                <div className="projects-new-skeleton-line short" />
                <div className="projects-new-card-metrics">
                    <div className="projects-new-skeleton-metric" />
                    <div className="projects-new-skeleton-metric" />
                </div>
                <div className="projects-new-card-actions">
                    <div className="projects-new-skeleton-btn" />
                    <div className="projects-new-skeleton-btn outline" />
                </div>
            </div>,
        );
    }
    return cards;
}

/**
 * swallowError
 * No-op error handler for background refreshes.
 */
function swallowError(): void
{
}

const PROJECT_NAME_CHAR_LIMIT = 22;
const PROJECTS_PER_PAGE = 9;

const PROJECT_TYPE_OPTIONS: CustomDropdownOption[] = [
    { value: '', label: 'All Types' },
    { value: 'simple', label: 'Simple' },
    { value: 'azure_devops', label: 'Azure DevOps' },
];

function truncateWithEllipsis(value: string, maxLength: number): string
{
    if (value.length <= maxLength)
    {
        return value;
    }
    return `${value.slice(0, maxLength - 3)}...`;
}

function legacyTestCaseTotal(stats: ProjectWithRepository['stats']): number
{
    return (stats?.negative_test_cases ?? 0) + (stats?.smoke_test_cases ?? 0) + (stats?.regression_test_cases ?? 0);
}

function visibleTestCaseCategories(stats: ProjectWithRepository['stats']): ProjectTestCaseCategorySummary[]
{
    const dynamicCategories = (stats?.test_case_categories || [])
        .filter((item) => Number(item.count || 0) > 0)
        .slice(0, 3);
    if (dynamicCategories.length > 0)
    {
        return dynamicCategories;
    }

    return [
        { category: 'Negative Testing', count: stats?.negative_test_cases ?? 0 },
        { category: 'Regression Testing', count: stats?.regression_test_cases ?? 0 },
        { category: 'Smoke & Sanity Testing', count: stats?.smoke_test_cases ?? 0 },
    ]
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count || a.category.localeCompare(b.category))
        .slice(0, 3);
}

function testCaseCategoryPillClass(category: string, index: number): string
{
    const normalized = category.toLowerCase();
    if (normalized.includes('negative'))
    {
        return 'projects-new-card-pill--tc-negative';
    }
    if (normalized.includes('regression'))
    {
        return 'projects-new-card-pill--tc-regression';
    }
    if (normalized.includes('smoke') || normalized.includes('sanity'))
    {
        return 'projects-new-card-pill--tc-smoke';
    }
    return `projects-new-card-pill--tc-top-${index % 3}`;
}

/* ============================================================
   Sub-component: ProjectCard
   ============================================================ */

interface ProjectCardProps
{
    project: ProjectWithRepository;
    canManage: boolean;
    onSelect: (projectId: number) => void;
    onRename: (project: ProjectWithRepository) => void;
    onDelete: (project: ProjectWithRepository) => void;
}

/**
 * ProjectCard
 * Displays a single project summary card.
 */
function ProjectCard(
    { project, canManage, onSelect, onRename, onDelete }: ProjectCardProps
): React.ReactElement
{
    const stats = project.stats;
    const projectTypeLabel = project.project_type === 'azure_devops' ? 'Azure DevOps' : 'Simple';
    const projectName = project.name || 'Untitled Project';
    const projectNameDisplay = truncateWithEllipsis(projectName, PROJECT_NAME_CHAR_LIMIT);

    const totalDocs = (stats?.domain_knowledge_docs ?? 0) + (stats?.enterprise_knowledge_docs ?? 0) + (stats?.requirements_docs ?? 0);
    const totalTc = stats?.test_cases_total ?? legacyTestCaseTotal(stats);
    const topTestCaseCategories = visibleTestCaseCategories(stats);

    /** handleSelectClick — opens the project */
    function handleSelectClick(e: React.MouseEvent<HTMLButtonElement>): void
    {
        const idText = e.currentTarget.dataset.projectId || '';
        const projectId = Number(idText);
        if (!Number.isFinite(projectId))
        {
            return;
        }
        onSelect(projectId);
    }

    /** handleDeleteClick — deletes the project */
    function handleDeleteClick(e: React.MouseEvent<HTMLButtonElement>): void
    {
        const idText = e.currentTarget.dataset.projectId || '';
        const projectId = Number(idText);
        if (!Number.isFinite(projectId))
        {
            return;
        }
        onDelete(project);
    }

    function handleRenameClick(): void
    {
        onRename(project);
    }

    return (
        <div className="projects-new-card">
            {/* Card header — title only */}
            <div className="projects-new-card-header">
                <div className="projects-new-card-title">
                    <span className="projects-new-card-icon">
                        <FolderFill />
                    </span>
                    <span title={projectName}>{projectNameDisplay}</span>
                </div>
            </div>

            {/* Meta row: org banner + project type pill on same line */}
            <div className="projects-new-card-meta-row">
                {project.organization_name && (
                    <div className="projects-new-card-org-banner">
                        <span>{project.organization_name}</span>
                    </div>
                )}
                <span className="projects-new-card-pill projects-new-card-pill--type">
                    {projectTypeLabel}
                </span>
            </div>

            {/* Documents summary */}
            <div className="projects-new-card-stats-section">
                <span className="projects-new-card-stats-label">{totalDocs} Documents</span>
                <div className="projects-new-card-pills projects-new-card-pills--all">
                    <span className="projects-new-card-pill projects-new-card-pill--domain">
                        {stats?.domain_knowledge_docs ?? 0} Domain
                    </span>
                    <span className="projects-new-card-pill projects-new-card-pill--enterprise">
                        {stats?.enterprise_knowledge_docs ?? 0} Enterprise
                    </span>
                    <span className="projects-new-card-pill projects-new-card-pill--requirements">
                        {stats?.requirements_docs ?? 0} Requirement
                    </span>
                </div>
            </div>

            {/* Test Cases summary */}
            <div className="projects-new-card-stats-section">
                <span className="projects-new-card-stats-label">{totalTc} Test Cases</span>
                <div className="projects-new-card-pills projects-new-card-pills--all">
                    {topTestCaseCategories.length > 0 ? (
                        topTestCaseCategories.map((item, index) => (
                            <span
                                key={item.category}
                                className={`projects-new-card-pill projects-new-card-pill--tc-category ${testCaseCategoryPillClass(item.category, index)}`}
                            >
                                <span>{item.count}</span>
                                <span className="projects-new-card-pill-label" title={item.category}>
                                    {item.category}
                                </span>
                            </span>
                        ))
                    ) : (
                        <span className="projects-new-card-pill projects-new-card-pill--tc-empty">
                            No categories yet
                        </span>
                    )}
                </div>
            </div>

            {/* Card actions */}
            <div className="projects-new-card-actions projects-new-card-actions--right">
                <button
                    type="button"
                    className="projects-new-primary-btn"
                    data-project-id={project.id}
                    onClick={handleSelectClick}
                >
                    <BoxArrowUpRight />
                    View
                </button>
                {canManage && (
                    <>
                        <button
                            type="button"
                            className="projects-new-outline-btn"
                            onClick={handleRenameClick}
                            title="Rename project"
                        >
                            <PencilSquare />
                            Rename
                        </button>
                        <button
                            type="button"
                            className="projects-new-danger-btn"
                            data-project-id={project.id}
                            onClick={handleDeleteClick}
                            title="Delete project"
                        >
                            <Trash />
                            Delete
                        </button>
                    </>
                )}
            </div>
        </div>
    );
}

/* ============================================================
   Main component: ProjectsNew
   ============================================================ */

/**
 * ProjectsNew
 * Redesigned project listing page with server-side search, filters, and pagination.
 */
function ProjectsNew(): React.ReactElement
{
    const navigate = useNavigate();
    const { user } = useAuth();
    const { deleteProject } = useProjects();

    const isAdmin = canAccessAdminArea(user);
    const isSuperAdmin = isSuperAdminUser(user);
    const isOrganizationAdmin = isActiveOrganizationAdmin(user);
    const activeOrgId = Number(user?.active_organization_id || user?.organization_id || 0) || 0;
    const hasActiveOrganizationScope = isSuperAdmin && activeOrgId > 0;
    const canCreateProjectForUser = canCreateProject(user);

    /* ---- Server-side data state ---- */
    const [items, setItems] = useState<ProjectWithRepository[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    /* ---- Filter state: input (what the user types) vs applied (committed to API) ---- */
    const [searchInput, setSearchInput] = useState('');
    const [appliedSearch, setAppliedSearch] = useState('');
    const [projectTypeInput, setProjectTypeInput] = useState('');
    const [appliedProjectType, setAppliedProjectType] = useState('');
    const [fromDateInput, setFromDateInput] = useState('');
    const [appliedFromDate, setAppliedFromDate] = useState('');
    const [toDateInput, setToDateInput] = useState('');
    const [appliedToDate, setAppliedToDate] = useState('');
    const [dateError, setDateError] = useState('');
    const [orgFilterInput, setOrgFilterInput] = useState('');
    const [appliedOrgFilter, setAppliedOrgFilter] = useState('');
    const [orgOptions, setOrgOptions] = useState<CustomDropdownOption[]>([]);
    const [renameTarget, setRenameTarget] = useState<ProjectWithRepository | null>(null);
    const [renameValue, setRenameValue] = useState('');
    const [deleteTarget, setDeleteTarget] = useState<ProjectWithRepository | null>(null);
    const [deleteImpact, setDeleteImpact] = useState<ProjectDeletionImpact | null>(null);
    const [impactLoading, setImpactLoading] = useState(false);
    const [actionBusy, setActionBusy] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);

    const totalPages = Math.max(1, Math.ceil(total / PROJECTS_PER_PAGE));

    /* ---- Load org options for superadmin ---- */
    useEffect(() =>
    {
        if (!isSuperAdmin || hasActiveOrganizationScope) { return; }
        fetchOrganizations(1, 200)
            .then((data) =>
            {
                const opts: CustomDropdownOption[] = [
                    { value: '', label: 'All Organizations' },
                    ...data.items.map((o) => ({ value: String(o.id), label: o.name })),
                ];
                setOrgOptions(opts);
            })
            .catch(() => undefined);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasActiveOrganizationScope, isSuperAdmin]);

    /* ---- Data loading ---- */

    async function loadProjects(): Promise<void>
    {
        setLoading(true);
        setError(null);
        try
        {
            const filters: ProjectsFilterParams = {};
            if (appliedSearch) filters.search = appliedSearch;
            if (appliedProjectType) filters.project_type = appliedProjectType;
            if (appliedFromDate) filters.date_from = appliedFromDate;
            if (appliedToDate) filters.date_to = appliedToDate;
            if (isSuperAdmin && !hasActiveOrganizationScope && appliedOrgFilter) filters.organization_id = Number(appliedOrgFilter);
            // Always scope to the user's active organization (backend enforces this for org admins too)
            if (!isAdmin)
            {
                const userActiveOrgId = user?.active_organization_id || user?.organization_id;
                if (userActiveOrgId) filters.organization_id = userActiveOrgId;
            }
            const data = await fetchProjectsPaged(page, PROJECTS_PER_PAGE, filters);
            setItems(data.items);
            setTotal(data.total);
        }
        catch (err: any)
        {
            setError(err?.response?.data?.detail || 'Failed to load projects');
        }
        finally
        {
            setLoading(false);
        }
    }

    /* ---- Effects ---- */

    useEffect(() =>
    {
        loadProjects();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, appliedSearch, appliedProjectType, appliedFromDate, appliedToDate, appliedOrgFilter, hasActiveOrganizationScope]);

    useEffect(() =>
    {
        if (!hasActiveOrganizationScope)
        {
            return;
        }
        setOrgFilterInput('');
        setAppliedOrgFilter('');
    }, [hasActiveOrganizationScope]);

    /* ---- Filter handlers ---- */

    function applyFilters(): void
    {
        if ((fromDateInput && !toDateInput) || (!fromDateInput && toDateInput))
        {
            setDateError('Please select both a start date and an end date.');
            return;
        }
        setDateError('');
        setAppliedSearch(searchInput);
        setAppliedProjectType(projectTypeInput);
        setAppliedFromDate(fromDateInput);
        setAppliedToDate(toDateInput);
        setAppliedOrgFilter(orgFilterInput);
        setPage(1);
    }

    function handleSearchKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void
    {
        if (e.key === 'Enter') { applyFilters(); }
    }

    function handleClearFilters(): void
    {
        setSearchInput('');
        setProjectTypeInput('');
        setFromDateInput('');
        setToDateInput('');
        setAppliedSearch('');
        setAppliedProjectType('');
        setAppliedFromDate('');
        setAppliedToDate('');
        setAppliedOrgFilter('');
        setOrgFilterInput('');
        setDateError('');
        setPage(1);
    }

    /* ---- Action handlers ---- */

    function handleCreateProject(): void
    {
        if (!canCreateProjectForUser)
        {
            return;
        }
        navigate('/projects/new');
    }

    function handleRefreshProjects(): void
    {
        loadProjects().catch(swallowError);
    }

    function handleSelectProject(projectId: number): void
    {
        navigate(`/projects/${projectId}`);
    }

    function canManageProject(project: ProjectWithRepository): boolean
    {
        return isSuperAdmin
            || isOrganizationAdmin
            || Number(project.user_id) === Number(user?.id);
    }

    function handleRenameProject(project: ProjectWithRepository): void
    {
        setActionError(null);
        setRenameTarget(project);
        setRenameValue(project.name);
    }

    async function confirmRenameProject(): Promise<void>
    {
        if (!renameTarget) { return; }
        const name = renameValue.trim();
        if (!name)
        {
            setActionError('Project name is required.');
            return;
        }
        setActionBusy(true);
        setActionError(null);
        try
        {
            const updated = await renameProject(renameTarget.id, name);
            setItems((current) => current.map((item) => (
                item.id === updated.id ? { ...item, ...updated } : item
            )));
            setRenameTarget(null);
        }
        catch (err: any)
        {
            setActionError(err?.response?.data?.detail || 'Unable to rename the project.');
        }
        finally
        {
            setActionBusy(false);
        }
    }

    async function handleDeleteProject(project: ProjectWithRepository): Promise<void>
    {
        setDeleteTarget(project);
        setDeleteImpact(null);
        setActionError(null);
        setImpactLoading(true);
        try
        {
            setDeleteImpact(await fetchProjectDeletionImpact(project.id));
        }
        catch (err: any)
        {
            setActionError(err?.response?.data?.detail || 'Unable to load the project deletion impact.');
        }
        finally
        {
            setImpactLoading(false);
        }
    }

    async function confirmDeleteProject(): Promise<void>
    {
        if (!deleteTarget || !deleteImpact) { return; }
        setActionBusy(true);
        setActionError(null);
        try
        {
            await deleteProject(deleteTarget.id);
            setDeleteTarget(null);
            setDeleteImpact(null);
            await loadProjects();
        }
        catch (err: any)
        {
            setActionError(err?.response?.data?.detail || 'Unable to delete the project.');
        }
        finally
        {
            setActionBusy(false);
        }
    }

    /* ---- Render helpers ---- */

    function renderFilters(): React.ReactElement
    {
        const hasActiveFilters = !!(appliedSearch || appliedProjectType || appliedFromDate || appliedToDate || appliedOrgFilter);

        return (
            <div className="projects-new-filters">
                {/* Search by name */}
                <div className="projects-new-filter-field projects-new-filter-search">
                    <svg className="projects-new-filter-search-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                        <circle cx="9" cy="9" r="6" stroke="currentColor" strokeWidth="1.8" />
                        <path d="M13.5 13.5L17 17" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                    </svg>
                    <input
                        className="projects-new-filter-input"
                        type="text"
                        placeholder="Search by project name…"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        onKeyDown={handleSearchKeyDown}
                    />
                </div>

                {/* Organization filter — super admin only */}
                {isSuperAdmin && !hasActiveOrganizationScope && (
                    <div className="projects-new-filter-field projects-new-filter-dropdown">
                        <CustomDropdown
                            value={orgFilterInput}
                            options={orgOptions}
                            onChange={(v) => setOrgFilterInput(v)}
                            placeholder="All Organizations"
                        />
                    </div>
                )}

                {/* Project type filter */}
                <div className="projects-new-filter-field projects-new-filter-dropdown">
                    <CustomDropdown
                        value={projectTypeInput}
                        options={PROJECT_TYPE_OPTIONS}
                        onChange={(v) => setProjectTypeInput(v)}
                        placeholder="All Types"
                    />
                </div>

                {/* Date range filter */}
                <div className="projects-new-filter-field projects-new-filter-date filter-date-ph-wrap">
                    <input
                        className={`projects-new-filter-date-input${!fromDateInput ? ' date-empty' : ''}${dateError ? ' projects-new-filter-date-input--error' : ''}`}
                        type="date"
                        aria-label="From date"
                        value={fromDateInput}
                        onChange={(e) => { setFromDateInput(e.target.value); setDateError(''); }}
                    />
                    {!fromDateInput && <span className="filter-date-ph" aria-hidden="true">Start Date</span>}
                </div>
                <div className="projects-new-filter-field projects-new-filter-date filter-date-ph-wrap">
                    <input
                        className={`projects-new-filter-date-input${!toDateInput ? ' date-empty' : ''}${dateError ? ' projects-new-filter-date-input--error' : ''}`}
                        type="date"
                        aria-label="To date"
                        value={toDateInput}
                        onChange={(e) => { setToDateInput(e.target.value); setDateError(''); }}
                    />
                    {!toDateInput && <span className="filter-date-ph" aria-hidden="true">End Date</span>}
                </div>
                {dateError && <span className="projects-new-filter-date-error">{dateError}</span>}

                {/* Apply search */}
                <button
                    className="projects-new-btn-search"
                    onClick={applyFilters}
                    type="button"
                    disabled={loading}
                >
                    Search
                </button>

                {/* Clear active filters */}
                {hasActiveFilters && (
                    <button
                        className="projects-new-btn-clear"
                        onClick={handleClearFilters}
                        type="button"
                    >
                        Clear
                    </button>
                )}
            </div>
        );
    }

    function buildProjectCards(): React.ReactElement[]
    {
        const cards: React.ReactElement[] = [];
        for (const project of items)
        {
            cards.push(
                <ProjectCard
                    key={project.id}
                    project={project}
                    canManage={canManageProject(project)}
                    onSelect={handleSelectProject}
                    onRename={handleRenameProject}
                    onDelete={handleDeleteProject}
                />,
            );
        }
        return cards;
    }

    /** renderEmptyState — shows an empty placeholder */
    function renderEmptyState(): React.ReactElement
    {
        return (
            <div className="projects-new-empty">
                <div className="projects-new-empty-icon">
                    <FolderFill />
                </div>
                <h3>No projects yet</h3>
                <p>Create a workspace to track requirements, repositories, and generated tests.</p>
                {canCreateProjectForUser && (
                    <button type="button" className="projects-new-primary-btn" onClick={handleCreateProject}>
                        <PlusCircle />
                        Create Project
                    </button>
                )}
            </div>
        );
    }

    function renderPagination(): React.ReactElement | null
    {
        if (loading || total === 0) { return null; }

        const startItem = (page - 1) * PROJECTS_PER_PAGE + 1;
        const endItem = Math.min(page * PROJECTS_PER_PAGE, total);
        const pageStart = Math.max(1, Math.min(page - 2, Math.max(1, totalPages - 4)));
        const pageEnd = Math.min(totalPages, pageStart + 4);
        const pageNumbers: number[] = [];

        for (let p = pageStart; p <= pageEnd; p += 1)
        {
            pageNumbers.push(p);
        }

        return (
            <div className="projects-new-pagination">
                <span className="projects-new-pagination-summary">
                    Showing {startItem}–{endItem} of {total} projects
                </span>

                <div className="projects-new-pagination-controls">
                    {/* First */}
                    <button
                        type="button"
                        className="projects-new-page-btn"
                        onClick={() => setPage(1)}
                        disabled={page === 1}
                    >
                        ← First
                    </button>

                    {/* Prev */}
                    <button
                        type="button"
                        className="projects-new-page-btn"
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                    >
                        Prev
                    </button>

                    {/* Page numbers */}
                    {pageNumbers.map((p) => (
                        <button
                            key={p}
                            type="button"
                            className={`projects-new-page-btn${p === page ? ' projects-new-page-btn--active' : ''}`}
                            onClick={() => setPage(p)}
                        >
                            {p}
                        </button>
                    ))}

                    {/* Next */}
                    <button
                        type="button"
                        className="projects-new-page-btn"
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                    >
                        Next
                    </button>

                    {/* Last */}
                    <button
                        type="button"
                        className="projects-new-page-btn"
                        onClick={() => setPage(totalPages)}
                        disabled={page === totalPages}
                    >
                        Last →
                    </button>
                </div>
            </div>
        );
    }

    /* ---- Render ---- */

    return (
        <div className="projects-new-page">
            {/* Hero header */}
            <div className="projects-new-hero">
                <div>
                    <p className="projects-new-eyebrow">Workspace</p>
                    <h1>Projects</h1>
                    <p className="projects-new-subtitle">
                        Organize requirement pipelines, repositories, and test automation in one place.
                    </p>
                </div>
                <div className="projects-new-hero-actions">
                    <button type="button" className="projects-new-outline-btn" onClick={handleRefreshProjects}>
                        <ArrowClockwise />
                        Refresh
                    </button>
                    {canCreateProjectForUser && (
                        <button id="projects-create-action" type="button" className="projects-new-primary-btn" onClick={handleCreateProject}>
                            <PlusCircle />
                            Create Project
                        </button>
                    )}
                </div>
            </div>

            {/* Error banner */}
            {error && (
                <Alert variant="danger" className="projects-new-alert">
                    {error}
                </Alert>
            )}

                        {/* Filter bar */}
            {renderFilters()}

            {/* Project grid */}
            <div className="projects-new-grid">
                {loading && buildSkeletonCards(6)}
                {!loading && items.length === 0 && renderEmptyState()}
                {!loading && items.length > 0 && buildProjectCards()}
            </div>

            {renderPagination()}

            <Modal
                show={renameTarget !== null}
                onHide={() => !actionBusy && setRenameTarget(null)}
                centered
            >
                <Modal.Header closeButton={!actionBusy}>
                    <Modal.Title>Rename Project</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group controlId="project-rename-input">
                        <Form.Label>Project name</Form.Label>
                        <Form.Control
                            value={renameValue}
                            maxLength={255}
                            autoFocus
                            disabled={actionBusy}
                            onChange={(event) => {
                                setRenameValue(event.target.value);
                                setActionError(null);
                            }}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter') {
                                    event.preventDefault();
                                    confirmRenameProject().catch(swallowError);
                                }
                            }}
                        />
                    </Form.Group>
                    {actionError && <Alert variant="danger" className="mt-3 mb-0">{actionError}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="projects-new-outline-btn"
                        disabled={actionBusy}
                        onClick={() => setRenameTarget(null)}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="projects-new-primary-btn"
                        disabled={actionBusy || !renameValue.trim()}
                        onClick={() => confirmRenameProject().catch(swallowError)}
                    >
                        <PencilSquare />
                        {actionBusy ? 'Renaming...' : 'Rename'}
                    </button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={deleteTarget !== null}
                onHide={() => !actionBusy && setDeleteTarget(null)}
                centered
            >
                <Modal.Header closeButton={!actionBusy}>
                    <Modal.Title>Delete Project</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {impactLoading && <p className="mb-0">Checking active jobs...</p>}
                    {!impactLoading && deleteImpact && (
                        <div className="projects-new-delete-impact">
                            <div className="projects-new-delete-warning">
                                <ExclamationTriangleFill aria-hidden="true" />
                                <strong>{deleteImpact.project_name} and its related data will be deleted.</strong>
                            </div>
                            {deleteImpact.running_jobs.length > 0 && (
                                <div className="projects-new-impact-group">
                                    <strong>Running jobs will be stopped and deleted:</strong>
                                    <span>{deleteImpact.running_jobs.map((job) => `#${job.id}`).join(', ')}</span>
                                </div>
                            )}
                            {deleteImpact.queued_jobs.length > 0 && (
                                <div className="projects-new-impact-group">
                                    <strong>Queued jobs will be deleted without running:</strong>
                                    <span>{deleteImpact.queued_jobs.map((job) => `#${job.id}`).join(', ')}</span>
                                </div>
                            )}
                        </div>
                    )}
                    {actionError && <Alert variant="danger" className="mt-3 mb-0">{actionError}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <button
                        type="button"
                        className="projects-new-outline-btn"
                        disabled={actionBusy}
                        onClick={() => setDeleteTarget(null)}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        className="projects-new-danger-btn"
                        disabled={actionBusy || impactLoading || !deleteImpact}
                        onClick={() => confirmDeleteProject().catch(swallowError)}
                    >
                        <Trash />
                        {actionBusy ? 'Deleting...' : 'Delete Project'}
                    </button>
                </Modal.Footer>
            </Modal>
        </div>
    );
}

export default ProjectsNew;
